import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/EcoAppsAuthContext';
import { propertyService, paymentService, contractService, notificationService, userService } from '../../../services/api';
import { PageContainer, PageHeader, PageSection } from '../../../components/Layout';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent,
  StatCard, 
  Badge, 
  StatusBadge,
  Button,
  Input,
  Skeleton,
  SkeletonCard,
  SkeletonStat,
  Spinner,
  EmptyState,
} from '../../../components/UI';
import {
  Building2,
  Home,
  CheckCircle,
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Plus,
  FileText,
  Wallet,
  AlertTriangle,
  Key,
  ArrowRight,
  Calendar,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';

/**
 * Dashboard Component - Main dashboard for RentalBiz application
 * 
 * Features:
 * - Stats cards with trends
 * - Revenue chart (landlord only)
 * - Pending payments list
 * - Quick actions panel
 * - Invitation code section (tenant only)
 */
const Dashboard = () => {
  const { user, isLandlord } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitationCode, setInvitationCode] = useState('');
  const [originalCode, setOriginalCode] = useState('');
  const [updatingCode, setUpdatingCode] = useState(false);
  const [codeMessage, setCodeMessage] = useState({ type: '', text: '' });
  const [hasActiveContract, setHasActiveContract] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);
  const [revenueData, setRevenueData] = useState([]);

  // Mock revenue data for chart (in real app, this would come from API)
  const mockRevenueData = [
    { month: 'Ene', revenue: 45000, expenses: 12000 },
    { month: 'Feb', revenue: 48000, expenses: 11000 },
    { month: 'Mar', revenue: 52000, expenses: 15000 },
    { month: 'Abr', revenue: 49000, expenses: 13000 },
    { month: 'May', revenue: 55000, expenses: 14000 },
    { month: 'Jun', revenue: 58000, expenses: 12000 },
    { month: 'Jul', revenue: 62000, expenses: 16000 },
    { month: 'Ago', revenue: 60000, expenses: 15000 },
    { month: 'Sep', revenue: 65000, expenses: 14000 },
    { month: 'Oct', revenue: 68000, expenses: 17000 },
    { month: 'Nov', revenue: 70000, expenses: 15000 },
    { month: 'Dic', revenue: 75000, expenses: 18000 },
  ];

  // Property status data for pie chart
  const propertyStatusData = [
    { name: 'Rentadas', value: stats?.rented || 0, color: '#22c55e' },
    { name: 'Disponibles', value: stats?.available || 0, color: '#eab308' },
    { name: 'Mantenimiento', value: stats?.maintenance || 0, color: '#ef4444' },
  ];

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, pendingRes, dashboardRes] = await Promise.all([
          isLandlord ? propertyService.getStats() : Promise.resolve({ data: { data: { stats: {} } } }),
          paymentService.getPending(),
          notificationService.getDashboard()
        ]);

        if (isLandlord) {
          setStats(statsRes.data.data.stats);
          setRevenueData(mockRevenueData);
        }

        setPendingPayments(pendingRes.data.data.payments || []);
        setUpcomingPayments(dashboardRes.data.data.upcomingPayments || []);
        setExpiringContracts(dashboardRes.data.data.expiringContracts || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isLandlord]);

  // Load user invitation code and check for active contracts
  useEffect(() => {
    const fetchUserInvitationCode = async () => {
      if (isLandlord || !user) return;

      try {
        setLoadingCode(true);
        
        const response = await userService.getById(user.id);
        const userData = response.data?.data || response.data;
        const code = userData.property_invitation_code || '';
        setInvitationCode(code);
        setOriginalCode(code);

        try {
          const contractsRes = await contractService.getAll({ type: 'tenant' });
          const allContracts = contractsRes.data?.data?.contracts || contractsRes.data?.data || [];
          
          const hasRelevantContract = allContracts.length > 0 && allContracts.some(c => 
            c.status === 'active' || 
            c.status === 'pending' || 
            c.status === 'renewed'
          );
          
          setHasActiveContract(hasRelevantContract);
        } catch (contractError) {
          console.error('Error fetching contracts:', contractError);
          setHasActiveContract(!!code);
        }

      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoadingCode(false);
      }
    };

    fetchUserInvitationCode();
  }, [isLandlord, user]);

  const handleCodeBlur = async () => {
    const trimmedCode = invitationCode.trim().toUpperCase();
    const trimmedOriginal = originalCode.trim().toUpperCase();
    
    if (trimmedCode === trimmedOriginal) return;
    if (isCodeDisabled) return;
    
    await saveInvitationCode(trimmedCode);
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedCode = invitationCode.trim().toUpperCase();
      await saveInvitationCode(trimmedCode);
    }
  };

  const saveInvitationCode = async (code) => {
    setUpdatingCode(true);
    setCodeMessage({ type: '', text: '' });

    if (!code) {
      try {
        await userService.updateInvitationCode(null);
        setOriginalCode('');
        setCodeMessage({ type: 'success', text: 'Código de invitación limpiado exitosamente' });
      } catch (error) {
        setCodeMessage({ 
          type: 'error', 
          text: error.response?.data?.message || 'Error al limpiar el código de invitación' 
        });
      } finally {
        setUpdatingCode(false);
      }
      return;
    }

    try {
      const validateRes = await propertyService.validateInvitationCode(code);
      
      if (!validateRes.data.valid) {
        setInvitationCode(originalCode);
        setCodeMessage({ 
          type: 'error', 
          text: validateRes.data.message || 'Código de invitación no válido.' 
        });
        setUpdatingCode(false);
        return;
      }

      await userService.updateInvitationCode(code);
      setOriginalCode(code);
      setCodeMessage({ 
        type: 'success', 
        text: `Código guardado para: ${validateRes.data.data.property.name}` 
      });
    } catch (error) {
      setInvitationCode(originalCode);
      
      if (error.response?.status === 404) {
        setCodeMessage({ 
          type: 'error', 
          text: 'No existe una propiedad con este código de invitación.' 
        });
      } else {
        setCodeMessage({ 
          type: 'error', 
          text: error.response?.data?.message || 'Error al guardar el código de invitación' 
        });
      }
    } finally {
      setUpdatingCode(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const isCodeDisabled = hasActiveContract && originalCode;

  // Loading state with skeletons
  if (loading) {
    return (
      <PageContainer>
        <div className="space-y-6">
          {/* Header skeleton */}
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>

          {/* Stats skeleton */}
          {isLandlord && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <SkeletonStat key={i} />
              ))}
            </div>
          )}

          {/* Content skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <SkeletonCard />
            </div>
            <SkeletonCard />
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Page Header */}
        <PageHeader
          title="Dashboard"
          subtitle={isLandlord 
            ? 'Resumen de tus propiedades y ingresos' 
            : 'Resumen de tus rentas y pagos'
          }
        />

        {/* Stats Cards - Landlord Only */}
        {isLandlord && stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            <StatCard
              title="Total Propiedades"
              value={stats.total || 0}
              icon={Building2}
              color="blue"
              trend={stats.totalTrend}
              trendLabel="vs mes anterior"
            />
            <StatCard
              title="Rentadas"
              value={stats.rented || 0}
              icon={CheckCircle}
              color="green"
              trend={stats.rentedTrend}
              trendLabel="vs mes anterior"
            />
            <StatCard
              title="Disponibles"
              value={stats.available || 0}
              icon={Clock}
              color="yellow"
              trend={stats.availableTrend}
              trendLabel="vs mes anterior"
            />
            <StatCard
              title="Ingresos Mensuales"
              value={formatCurrency(stats.totalMonthlyRent)}
              icon={DollarSign}
              color="purple"
              trend={stats.incomeTrend}
              trendLabel="vs mes anterior"
            />
          </div>
        )}

        {/* Tenant Invitation Code Section */}
        {!isLandlord && (
          <Card className="bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary-200 rounded-lg">
                      <Key className="w-6 h-6 text-primary-700" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Código de Invitación</h2>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {isCodeDisabled
                          ? 'Tu código está vinculado a tu propiedad actual.'
                          : 'Ingresa el código que te proporcionó tu arrendador.'}
                      </p>
                    </div>
                  </div>
                  
                  {loadingCode ? (
                    <div className="mt-4 flex items-center justify-center py-4">
                      <Spinner size="md" />
                    </div>
                  ) : (
                    <div className="mt-4">
                      <div className="flex gap-3 items-center">
                        <Input
                          value={invitationCode}
                          onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                          onBlur={handleCodeBlur}
                          onKeyDown={handleKeyDown}
                          placeholder="PROP-XXXXXX"
                          className="flex-1 uppercase"
                          disabled={isCodeDisabled || updatingCode}
                        />
                        {updatingCode && <Spinner size="sm" />}
                      </div>
                      
                      {isCodeDisabled && (
                        <div className="mt-2 flex items-center text-sm text-green-600">
                          <CheckCircle className="w-4 h-4 mr-1.5" />
                          Contrato activo - Código vinculado
                        </div>
                      )}
                      
                      {codeMessage.text && (
                        <div className={`mt-3 p-3 rounded-lg text-sm ${
                          codeMessage.type === 'success' 
                            ? 'bg-green-100 text-green-700 border border-green-200' 
                            : 'bg-red-100 text-red-700 border border-red-200'
                        }`}>
                          {codeMessage.text}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Charts Section - Landlord Only */}
        {isLandlord && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Revenue Chart */}
            <Card className="lg:col-span-2 min-w-0">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-primary-600" />
                  Ingresos vs Gastos
                </CardTitle>
              </CardHeader>
              <CardContent className="min-w-0">
                <div className="h-80 min-h-[320px] min-w-0">
                  <ResponsiveContainer width="100%" height={320} minWidth={0}>
                    <AreaChart data={revenueData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                      <YAxis 
                        stroke="#6b7280" 
                        fontSize={12}
                        tickFormatter={(value) => `$${value/1000}k`}
                      />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        name="Ingresos"
                        stroke="#3b82f6" 
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                      />
                      <Area 
                        type="monotone" 
                        dataKey="expenses" 
                        name="Gastos"
                        stroke="#ef4444" 
                        fillOpacity={1} 
                        fill="url(#colorExpenses)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Property Status Chart */}
            <Card className="min-w-0">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="w-5 h-5 mr-2 text-primary-600" />
                  Estado de Propiedades
                </CardTitle>
              </CardHeader>
              <CardContent className="min-w-0">
                <div className="h-64 min-h-[256px] min-w-0">
                  <ResponsiveContainer width="100%" height={256} minWidth={0}>
                    <PieChart>
                      <Pie
                        data={propertyStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {propertyStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => `${value} propiedades`}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pending Payments */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center">
                <Wallet className="w-5 h-5 mr-2 text-primary-600" />
                Pagos Pendientes
              </CardTitle>
              <Link 
                to="/rentalbiz/payments" 
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center"
              >
                Ver todos
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </CardHeader>
            <CardContent>
              {pendingPayments.length === 0 ? (
                <EmptyState
                  icon={Wallet}
                  title="No hay pagos pendientes"
                  description="Todos tus pagos están al día"
                  size="sm"
                />
              ) : (
                <div className="space-y-3">
                  {pendingPayments.slice(0, 5).map((payment) => {
                    const isOverdue = new Date(payment.due_date) < new Date();
                    
                    return (
                      <div 
                        key={payment.id} 
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-4">
                          <div className={`p-2.5 rounded-lg ${
                            isOverdue ? 'bg-red-100' : 'bg-yellow-100'
                          }`}>
                            <Calendar className={`w-5 h-5 ${
                              isOverdue ? 'text-red-600' : 'text-yellow-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {payment.contract?.property?.name || 'Propiedad'}
                            </p>
                            <p className="text-sm text-gray-500">
                              Vence: {new Date(payment.due_date).toLocaleDateString('es-MX', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(payment.amount)}
                          </p>
                          <StatusBadge 
                            status={isOverdue ? 'overdue' : 'pending'} 
                            size="sm"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Acciones Rápidas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Link
                  to={isLandlord ? "/rentalbiz/properties/new" : "/rentalbiz/properties"}
                  className="flex items-center p-3 bg-primary-50 text-primary-700 rounded-xl hover:bg-primary-100 transition-colors group"
                >
                  <div className="p-2 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                    <Plus className="w-5 h-5" />
                  </div>
                  <span className="ml-3 font-medium">
                    {isLandlord ? 'Agregar Propiedad' : 'Buscar Propiedad'}
                  </span>
                </Link>
                
                {isLandlord && (
                  <Link
                    to="/rentalbiz/contracts/new"
                    className="flex items-center p-3 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors group"
                  >
                    <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
                      <FileText className="w-5 h-5" />
                    </div>
                    <span className="ml-3 font-medium">Nuevo Contrato</span>
                  </Link>
                )}
                
                <Link
                  to="/rentalbiz/payments"
                  className="flex items-center p-3 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors group"
                >
                  <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
                    <Wallet className="w-5 h-5" />
                  </div>
                  <span className="ml-3 font-medium">Ver Pagos</span>
                </Link>

                {isLandlord && expiringContracts.length > 0 && (
                  <Link
                    to="/rentalbiz/contracts"
                    className="flex items-center p-3 bg-red-50 text-red-700 rounded-xl hover:bg-red-100 transition-colors group"
                  >
                    <div className="p-2 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                      <AlertTriangle className="w-5 h-5" />
                    </div>
                    <span className="ml-3 font-medium">
                      Contratos por Vencer ({expiringContracts.length})
                    </span>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
};

export default Dashboard;
